import { Composition, Folder } from "remotion";
import { getCompetitions } from "./data/competitions";
import {
  MatchIntro,
  MatchResult,
  HighlightVideo,
  HIGHLIGHT_VIDEO_CONFIG,
  HighlightVideoV2,
  HighlightVideoV2PropsSchema,
  HIGHLIGHT_VIDEO_V2_CONFIG,
  calculateVideoFrames,
  HighlightVideoV3,
  HighlightVideoV3PropsSchema,
  HIGHLIGHT_VIDEO_V3_CONFIG,
  calculateVideoFramesV3,
  HighlightVideoV4,
  HighlightVideoV4PropsSchema,
  HIGHLIGHT_VIDEO_V4_CONFIG,
  calculateVideoFramesV4,
  HighlightVideoV5,
  HighlightVideoV5PropsSchema,
  HIGHLIGHT_VIDEO_V5_CONFIG,
  calculateVideoFramesV5,
  HighlightVideoV6,
  HighlightVideoV6PropsSchema,
  HIGHLIGHT_VIDEO_V6_CONFIG,
  calculateVideoFramesV6,
  HighlightVideoV7,
  HighlightVideoV7PropsSchema,
  HIGHLIGHT_VIDEO_V7_CONFIG,
  calculateVideoFramesV7,
  HighlightVideoV8,
  HighlightVideoV8PropsSchema,
  HIGHLIGHT_VIDEO_V8_CONFIG,
  calculateVideoFramesV8,
  HighlightVideoV9,
  HighlightVideoV9PropsSchema,
  HIGHLIGHT_VIDEO_V9_CONFIG,
  calculateVideoFramesV9,
  HighlightVideoV10,
  HighlightVideoV10PropsSchema,
  HIGHLIGHT_VIDEO_V10_CONFIG,
  calculateVideoFramesV10,
  HighlightVideoV11,
  HighlightVideoV11PropsSchema,
  HIGHLIGHT_VIDEO_V11_CONFIG,
  calculateVideoFramesV11,
} from "./templates";
import { VideoPropsSchema } from "./types/competition";

// 视频配置
const VIDEO_CONFIG = {
  width: 1920,
  height: 1080,
  fps: 30,
};

// 动态生成 Compositions
export const RemotionRoot: React.FC = () => {
  const competitions = getCompetitions();

  return (
    <>
      {/* 按比赛分组 - 每个比赛一个 Folder */}
      {competitions.map((competition) => (
        <Folder key={competition.id} name={competition.id}>
          {/* 比赛开场 */}
          <Composition
            id={`${competition.id}-intro`}
            component={MatchIntro}
            durationInFrames={150} // 5 秒
            fps={VIDEO_CONFIG.fps}
            width={VIDEO_CONFIG.width}
            height={VIDEO_CONFIG.height}
            schema={VideoPropsSchema}
            defaultProps={{
              competition,
            }}
          />

          {/* 比赛结果 */}
          <Composition
            id={`${competition.id}-result`}
            component={MatchResult}
            durationInFrames={180} // 6 秒
            fps={VIDEO_CONFIG.fps}
            width={VIDEO_CONFIG.width}
            height={VIDEO_CONFIG.height}
            schema={VideoPropsSchema}
            defaultProps={{
              competition,
            }}
          />
        </Folder>
      ))}

      {/* DOTA2 超神集锦 */}
      <Folder name="dota2-highlights">
        <Composition
          id="highlight-video"
          component={HighlightVideo}
          durationInFrames={HIGHLIGHT_VIDEO_CONFIG.durationInFrames}
          fps={HIGHLIGHT_VIDEO_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_CONFIG.width}
          height={HIGHLIGHT_VIDEO_CONFIG.height}
        />

        {/* V2 - 支持动态传入视频列表 */}
        <Composition
          id="highlight-video-v2"
          component={HighlightVideoV2}
          schema={HighlightVideoV2PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFrames(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V2_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V2_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V2_CONFIG.height}
          defaultProps={{
            videos: [
              { url: "https://cdn.xatlaz.com/remotion/videos/2025_12_07_15_16_54_highlight_0_multikill_852-869.mp4", duration: 22.833333, startFrom: 5 },
              { url: "https://cdn.xatlaz.com/remotion/videos/2025_12_07_15_18_36_highlight_2_multikill_1114-1131.mp4", duration: 22.833333, startFrom: 3 },
              { url: "https://cdn.xatlaz.com/remotion/videos/2025_12_07_15_19_36_highlight_3_multikill_1498-1506.mp4", duration: 13.833333, startFrom: 2 },
              { url: "https://cdn.xatlaz.com/remotion/videos/2025_12_07_15_20_17_highlight_4_multikill_1640-1651.mp4", duration: 16.833333, startFrom: 3 },
              { url: "https://cdn.xatlaz.com/remotion/videos/2025_12_07_15_21_03_highlight_5_multikill_1661-1676.mp4", duration: 20.833333, startFrom: 4 },
            ],
            // 卡点节拍时间点（秒）- INVINCIBLE.mp3 BPM=99.4
            // 每 8 拍（约 4.8 秒）切换一次视频
            beatTimestamps: [0, 5.46, 10.24, 15.05, 19.83, 24.64],
            audioUrl: "https://cdn.xatlaz.com/remotion/audio/INVINCIBLE.mp3",
            audioStartFrom: 10,
            audioVolume: 0.7,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            title: "DOTA2",
            subtitle: "超神集锦",
            brandName: "@超神时刻",
            brandSubtitle: "GODLIKE MOMENTS",
          }}
        />

        {/* V3 - 新模板：支持高光预览（慢镜头 + 放大聚焦） */}
        <Composition
          id="highlight-video-v3"
          component={HighlightVideoV3}
          schema={HighlightVideoV3PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV3(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V3_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V3_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V3_CONFIG.height}
          defaultProps={{
            videos: [
              { url: "https://cdn.xatlaz.com/remotion/videos/2025_12_11_22_47_02_highlight_0_multikill_1930-1966.mp4", duration: 41, startFrom: 0 },
            ],
            audioUrl: "https://cdn.xatlaz.com/remotion/audio/INVINCIBLE.mp3",
            audioStartFrom: 10,
            audioVolume: 0.7,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            // 高光预览配置
            previewEnabled: true,
            previewDuration: 1,      // 截取最后 1 秒
            previewSlowRate: 1 / 2,    // 3 倍慢放
            previewZoom: 5,        // 1.8 倍放大
            previewFocusX: 50,       // 水平居中
            previewFocusY: 25,       // 垂直上部
            title: "DOTA2",
            subtitle: "琼英碧灵RAMPAGE",
            brandName: "@超神时刻",
            brandSubtitle: "@NP.reneemartin",
          }}
        />

        {/* V4 - 新模板 */}
        <Composition
          id="highlight-video-v4"
          component={HighlightVideoV4}
          schema={HighlightVideoV4PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV4(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V4_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V4_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V4_CONFIG.height}
          defaultProps={{
            videos: [
              { url: "https://cdn.xatlaz.com/remotion/videos/2025_12_12_01_29_00_highlight_0_multikill_1508-1532.mp4", duration: 29, startFrom: 0 },
            ],
            audioUrl: "https://cdn.xatlaz.com/remotion/audio/INVINCIBLE.mp3",
            audioStartFrom: 10,
            audioVolume: 0.7,
            videoScale: 1.6,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            previewEnabled: true,
            previewDuration: 1,
            previewSlowRate: 1 / 2,
            previewZoom: 5,
            previewFocusX: 50,
            previewFocusY: 25,
            title: "",
            subtitle: "露娜暴走大杀四方",
            brandName: "",
            brandSubtitle: "",
          }}
        />

        {/* V5 */}
        <Composition
          id="highlight-video-v5"
          component={HighlightVideoV5}
          schema={HighlightVideoV5PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV5(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V5_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V5_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V5_CONFIG.height}
          defaultProps={{
            videos: [
              { url: "https://cdn.xatlaz.com/remotion/videos/2025_12_12_01_33_54_highlight_0_multikill_1357-1378.mp4", duration: 26, startFrom: 0 },
            ],
            audioUrl: "https://cdn.xatlaz.com/remotion/audio/INVINCIBLE.mp3",
            audioStartFrom: 10,
            audioVolume: 0.7,
            videoScale: 1.6,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            previewEnabled: false,
            previewDuration: 1,
            previewSlowRate: 1 / 2,
            previewZoom: 5,
            previewFocusX: 50,
            previewFocusY: 25,
            title: "",
            subtitle: "风暴之灵上蹿下跳五杀",
            brandName: "",
            brandSubtitle: "",
          }}
        />

        {/* V6 */}
        <Composition
          id="highlight-video-v6"
          component={HighlightVideoV6}
          schema={HighlightVideoV6PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV6(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V6_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V6_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V6_CONFIG.height}
          defaultProps={{
            videos: [
              {
                url: "https://cdn.xatlaz.com/remotion/videos/2025_12_13_15_50_28_highlight_0_multikill_1836-1886.mp4",
                duration: 55,
                startFrom: 0,
                speedSegments: [{ startTime: 39, endTime: 51, rate: 2 }],
              },
            ],
            audioUrl: "https://cdn.xatlaz.com/remotion/audio/INVINCIBLE.mp3",
            audioStartFrom: 10,
            audioVolume: 0.3,
            videoScale: 1.6,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            previewEnabled: true,
            previewDuration: 1,
            previewSlowRate: 1 / 2,
            previewZoom: 5,
            previewFocusX: 50,
            previewFocusY: 25,
            title: "",
            subtitle: "王牌打工人食尸鬼5杀",
            brandName: "",
            brandSubtitle: "每日追踪高分局五杀@超神时刻",
            textOverlays: [
            ],
          }}
        />

        {/* V7 */}
        <Composition
          id="highlight-video-v7"
          component={HighlightVideoV7}
          schema={HighlightVideoV7PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV7(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V7_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V7_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V7_CONFIG.height}
          defaultProps={{
            videos: [
              {
                url: "https://cdn.xatlaz.com/remotion/videos/2025_12_16_23_50_38_highlight_0_multikill_2057-2104.mp4",
                duration: 52,
                startFrom: 0,
                cutSegments: [
                  { startTime: 0, endTime: 3 },  
                ],
                speedSegments: [{ startTime: 19, endTime: 27.5, rate: 4 }],
              },
            ],
            videoScale: 1.6,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            previewEnabled: true,
            previewDuration: 2,
            previewSlowRate: 1,
            previewZoom: 1,
            previewFocusX: 50,
            previewFocusY: 50,
            title: "@超神时刻",
            subtitle: "",
            brandName: "打团？对面会自己倒",
            brandSubtitle: "",
            textOverlays: [
            ],
          }}
        />

        {/* V8 */}
        <Composition
          id="highlight-video-v8"
          component={HighlightVideoV8}
          schema={HighlightVideoV8PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV8(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V8_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V8_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V8_CONFIG.height}
          defaultProps={{
            videos: [
              {
                url: "https://cdn.xatlaz.com/remotion/videos/2025_12_28_20_15_26_highlight_0_multikill_2296-2302.mp4",
                duration: 12,
                startFrom: 4,
              },
            ],
            videoScale: 1.6,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            previewEnabled: true,
            previewDuration: 1,
            previewSlowRate: 0.5,
            previewZoom: 5,
            previewFocusX: 50,
            previewFocusY: 25,
            title: "@超神时刻",
            subtitle: "",
            brandName: "4杀! 这谁顶得住",
            brandSubtitle: "",
            textOverlays: [],
            audioUrl: "https://cdn.xatlaz.com/voiceover/2025_12_28_20_16_32_48d1b438-8333-4d84-86ce-b177d7c9a397.mp3",
          }}
        />

        {/* V9 */}
        <Composition
          id="highlight-video-v9"
          component={HighlightVideoV9}
          schema={HighlightVideoV9PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV9(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V9_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V9_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V9_CONFIG.height}
          defaultProps={{
            videos: [
              {
                url: "https://cdn.xatlaz.com/remotion/videos/2025_12_28_20_15_26_highlight_0_multikill_2296-2302.mp4",
                duration: 12,
                startFrom: 4,
              },
            ],
            videoScale: 1.6,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            previewEnabled: false,
            previewDuration: 1,
            previewSlowRate: 0.5,
            previewZoom: 5,
            previewFocusX: 50,
            previewFocusY: 25,
            title: "@超神时刻",
            subtitle: "",
            brandName: "V9 模板",
            brandSubtitle: "",
            textOverlays: [],
            audioUrl: "https://cdn.xatlaz.com/voiceover/2025_12_28_20_16_32_48d1b438-8333-4d84-86ce-b177d7c9a397.mp3",
            audioStartFrom: 0,
            audioVolume: 0.45,
            videoVolume: 0.1,
            introText: "2024 TI 大师赛\n精彩团战集锦",
          }}
        />

        {/* V10 */}
        <Composition
          id="highlight-video-v10"
          component={HighlightVideoV10}
          schema={HighlightVideoV10PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV10(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V10_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V10_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V10_CONFIG.height}
          defaultProps={{
            videos: [
              {
                url: "https://cdn.xatlaz.com/remotion/videos/2025_12_28_20_15_26_highlight_0_multikill_2296-2302.mp4",
                duration: 12,
                startFrom: 4,
              },
            ],
            videoScale: 1.6,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            previewEnabled: false,
            previewDuration: 1,
            previewSlowRate: 0.5,
            previewZoom: 5,
            previewFocusX: 50,
            previewFocusY: 25,
            title: "@超神时刻",
            subtitle: "",
            brandName: "V10 模板",
            brandSubtitle: "",
            textOverlays: [],
            audioUrl: "https://cdn.xatlaz.com/voiceover/2025_12_28_20_16_32_48d1b438-8333-4d84-86ce-b177d7c9a397.mp3",
            audioStartFrom: 0,
            audioVolume: 0.45,
            videoVolume: 0.1,
            introText: "2024 TI 大师赛\n精彩团战集锦",
          }}
        />

        {/* V11 */}
        <Composition
          id="highlight-video-v11"
          component={HighlightVideoV11}
          schema={HighlightVideoV11PropsSchema}
          calculateMetadata={({ props }) => {
            const { totalFrames } = calculateVideoFramesV11(
              props.videos,
              props.beatTimestamps,
              props.autoBeatAlign,
              props.bpm,
              props.previewEnabled,
              props.previewDuration,
              props.previewSlowRate
            );
            return {
              durationInFrames: totalFrames,
            };
          }}
          fps={HIGHLIGHT_VIDEO_V11_CONFIG.fps}
          width={HIGHLIGHT_VIDEO_V11_CONFIG.width}
          height={HIGHLIGHT_VIDEO_V11_CONFIG.height}
          defaultProps={{
            videos: [
              {
                url: "https://cdn.xatlaz.com/remotion/videos/2025_12_28_20_15_26_highlight_0_multikill_2296-2302.mp4",
                duration: 12,
                startFrom: 4,
              },
            ],
            videoScale: 1.6,
            autoBeatAlign: false,
            bpm: 99.4,
            loopBack: false,
            loopBackStartFrom: 0,
            previewEnabled: false,
            previewDuration: 1,
            previewSlowRate: 0.5,
            previewZoom: 5,
            previewFocusX: 50,
            previewFocusY: 25,
            title: "@超神时刻",
            subtitle: "",
            brandName: "V11 模板",
            brandSubtitle: "",
            textOverlays: [],
            audioUrl: "https://cdn.xatlaz.com/voiceover/2025_12_28_20_16_32_48d1b438-8333-4d84-86ce-b177d7c9a397.mp3",
            audioStartFrom: 0,
            audioVolume: 0.45,
            videoVolume: 0.1,
            introText: "2024 TI 大师赛\n精彩团战集锦",
            backgroundAudioUrl: "http://cdn.xatlaz.com/remotion/audio/INVINCIBLE.mp3",
            backgroundAudioStartFrom: 0,
            backgroundAudioVolume: 0.3,
          }}
        />
      </Folder>

      {/* 通用模板 - 可以通过 props 传入任意比赛数据 */}
      <Folder name="templates">
        <Composition
          id="generic-intro"
          component={MatchIntro}
          durationInFrames={150}
          fps={VIDEO_CONFIG.fps}
          width={VIDEO_CONFIG.width}
          height={VIDEO_CONFIG.height}
          schema={VideoPropsSchema}
          defaultProps={{
            competition: {
              id: "template",
              name: "比赛名称",
              date: "2024-01-01",
              location: "比赛地点",
              teams: [
                { name: "主队", score: 0 },
                { name: "客队", score: 0 },
              ],
            },
          }}
        />

        <Composition
          id="generic-result"
          component={MatchResult}
          durationInFrames={180}
          fps={VIDEO_CONFIG.fps}
          width={VIDEO_CONFIG.width}
          height={VIDEO_CONFIG.height}
          schema={VideoPropsSchema}
          defaultProps={{
            competition: {
              id: "template",
              name: "比赛名称",
              date: "2024-01-01",
              location: "比赛地点",
              teams: [
                { name: "主队", score: 0 },
                { name: "客队", score: 0 },
              ],
            },
          }}
        />
      </Folder>
    </>
  );
};
